/**
 * Stage 12 (A1): dependency-free phase vocoder for pitch shifting.
 *
 * Algorithm: STFT → phase-unwrap time-stretch by `alpha = 1/pitch` → linear
 * resample back to the original length. Net effect: pitch is shifted by
 * `pitch` while duration is preserved. Replaces the Stage 8 linear-only
 * resample which coupled pitch and rate.
 *
 *   FRAME = 2048 samples (~85 ms at 24 kHz)
 *   HOP_IN = 512 samples (75% overlap, Hann window)
 *   HOP_OUT = round(HOP_IN * alpha)
 *
 * Radix-2 Cooley-Tukey FFT, iterative bit-reversal, in-place on real/imag
 * buffers of length FRAME. No external libraries.
 */

const FRAME = 2048;
const HOP_IN = 512;
const TWO_PI = Math.PI * 2;

// ── Precomputed Hann window ─────────────────────────────────────────────
const HANN: Float32Array = (() => {
  const w = new Float32Array(FRAME);
  for (let i = 0; i < FRAME; i++) {
    w[i] = 0.5 * (1 - Math.cos((TWO_PI * i) / (FRAME - 1)));
  }
  return w;
})();

// ── Bit-reversal table for FRAME = 2048 (log2 = 11) ────────────────────
const LOG2_FRAME = Math.log2(FRAME);
const BITREV: Uint32Array = (() => {
  const arr = new Uint32Array(FRAME);
  for (let i = 0; i < FRAME; i++) {
    let x = i;
    let r = 0;
    for (let b = 0; b < LOG2_FRAME; b++) {
      r = (r << 1) | (x & 1);
      x >>>= 1;
    }
    arr[i] = r;
  }
  return arr;
})();

// ── Precomputed twiddle factors: cos/sin for each butterfly size ───────
// For each stage s (size = 2^(s+1)), the butterfly rotates by
// exp(-i * 2π * k / size). We store all needed angles once.
interface TwiddleStage {
  size: number;
  half: number;
  cos: Float32Array;
  sin: Float32Array;
}
const TWIDDLES: TwiddleStage[] = (() => {
  const stages: TwiddleStage[] = [];
  for (let s = 0; s < LOG2_FRAME; s++) {
    const size = 1 << (s + 1);
    const half = size >> 1;
    const cos = new Float32Array(half);
    const sin = new Float32Array(half);
    for (let k = 0; k < half; k++) {
      const angle = (-TWO_PI * k) / size;
      cos[k] = Math.cos(angle);
      sin[k] = Math.sin(angle);
    }
    stages.push({ size, half, cos, sin });
  }
  return stages;
})();

/** In-place radix-2 Cooley-Tukey FFT (forward). Arrays must be length FRAME. */
function fft(real: Float32Array, imag: Float32Array): void {
  // Bit-reversal permutation.
  for (let i = 0; i < FRAME; i++) {
    const j = BITREV[i];
    if (i < j) {
      let t = real[i];
      real[i] = real[j];
      real[j] = t;
      t = imag[i];
      imag[i] = imag[j];
      imag[j] = t;
    }
  }
  // Iterative butterflies.
  for (const stage of TWIDDLES) {
    const { size, half, cos, sin } = stage;
    for (let start = 0; start < FRAME; start += size) {
      for (let k = 0; k < half; k++) {
        const i0 = start + k;
        const i1 = i0 + half;
        const wr = cos[k];
        const wi = sin[k];
        const tr = real[i1] * wr - imag[i1] * wi;
        const ti = real[i1] * wi + imag[i1] * wr;
        real[i1] = real[i0] - tr;
        imag[i1] = imag[i0] - ti;
        real[i0] = real[i0] + tr;
        imag[i0] = imag[i0] + ti;
      }
    }
  }
}

/** In-place inverse FFT using the conjugate trick. */
function ifft(real: Float32Array, imag: Float32Array): void {
  for (let i = 0; i < FRAME; i++) imag[i] = -imag[i];
  fft(real, imag);
  const scale = 1 / FRAME;
  for (let i = 0; i < FRAME; i++) {
    real[i] *= scale;
    imag[i] = -imag[i] * scale;
  }
}

/** Wrap an angle into (-π, π]. */
function wrapPi(x: number): number {
  while (x > Math.PI) x -= TWO_PI;
  while (x <= -Math.PI) x += TWO_PI;
  return x;
}

/**
 * Time-stretch `pcm` by factor `alpha` (>1 = longer, <1 = shorter).
 * Preserves pitch — this is the core of the phase vocoder.
 */
function timeStretch(pcm: Float32Array, alpha: number): Float32Array {
  const hopOut = Math.max(1, Math.round(HOP_IN * alpha));
  const numFrames = Math.max(1, Math.floor((pcm.length - FRAME) / HOP_IN) + 1);
  const outLen = (numFrames - 1) * hopOut + FRAME;
  const out = new Float32Array(outLen);

  // Per-bin state: previous analysis phase, accumulated synthesis phase.
  const halfBins = FRAME / 2 + 1;
  const prevPhase = new Float32Array(halfBins);
  const sumPhase = new Float32Array(halfBins);

  const real = new Float32Array(FRAME);
  const imag = new Float32Array(FRAME);

  // Synthesis normalisation: Hann-window overlap-add at 75% overlap with an
  // additional Hann window on output sums to a small constant factor. We
  // track a weights buffer and divide at the end.
  const weights = new Float32Array(outLen);

  for (let f = 0; f < numFrames; f++) {
    const inStart = f * HOP_IN;
    // Windowed analysis frame.
    for (let i = 0; i < FRAME; i++) {
      real[i] = pcm[inStart + i] * HANN[i];
      imag[i] = 0;
    }
    fft(real, imag);

    // Phase-unwrap each positive-frequency bin and re-synthesize phase for
    // the output hop. Mirror bins (FRAME-k) are the conjugates of bins k;
    // we compute bins 0..FRAME/2 and mirror on the way back.
    const omegaK = (TWO_PI * HOP_IN) / FRAME;
    for (let k = 0; k <= FRAME / 2; k++) {
      const re = real[k];
      const im = imag[k];
      const mag = Math.hypot(re, im);
      const phase = Math.atan2(im, re);
      const expected = k * omegaK;
      const deviation = wrapPi(phase - prevPhase[k] - expected);
      const trueOmega = expected + deviation; // per hop_in
      const stepOut = (trueOmega * hopOut) / HOP_IN; // per hop_out
      sumPhase[k] = wrapPi(sumPhase[k] + stepOut);
      prevPhase[k] = phase;
      real[k] = mag * Math.cos(sumPhase[k]);
      imag[k] = mag * Math.sin(sumPhase[k]);
    }
    // Mirror the upper half from the conjugate of the lower half.
    for (let k = 1; k < FRAME / 2; k++) {
      real[FRAME - k] = real[k];
      imag[FRAME - k] = -imag[k];
    }

    ifft(real, imag);

    // Windowed overlap-add. Synthesis window = Hann again so total
    // weighting at each output sample is a predictable sum.
    const outStart = f * hopOut;
    for (let i = 0; i < FRAME; i++) {
      const idx = outStart + i;
      if (idx < 0 || idx >= outLen) continue;
      const w = HANN[i];
      out[idx] += real[i] * w;
      weights[idx] += w * w;
    }
  }

  // Normalise by the per-sample Hann² overlap sum (with floor to avoid /0
  // at boundary samples).
  for (let i = 0; i < outLen; i++) {
    const w = weights[i];
    if (w > 1e-6) out[i] /= w;
  }

  return out;
}

/** Linear-interpolation resample `pcm` to `targetLength` samples. */
function linearResample(
  pcm: Float32Array,
  targetLength: number,
): Float32Array {
  if (targetLength === pcm.length) return pcm;
  const out = new Float32Array(Math.max(1, targetLength));
  const ratio = (pcm.length - 1) / Math.max(1, targetLength - 1);
  for (let i = 0; i < out.length; i++) {
    const srcF = i * ratio;
    const i0 = Math.floor(srcF);
    const i1 = Math.min(pcm.length - 1, i0 + 1);
    const frac = srcF - i0;
    out[i] = pcm[i0] * (1 - frac) + pcm[i1] * frac;
  }
  return out;
}

/**
 * Stage 12 (A1) pitch-shift entry point. Returns a new Float32Array with
 * the same length as `pcm`. Pitch `p = 1.0` returns the input reference
 * unchanged (fast path).
 */
export function shiftPitch(pcm: Float32Array, pitch: number): Float32Array {
  const p = Math.max(0.5, Math.min(1.5, pitch));
  if (Math.abs(p - 1) < 1e-4) return pcm;
  // Inputs shorter than one frame can't be phase-vocoded; fall back to
  // linear resample back to original length (zero actual shift then).
  if (pcm.length < FRAME) return pcm;
  // Time-stretch by `p` (preserves pitch, length becomes length * p).
  // Then linear-resample back to original length, which compresses by
  // factor p and — as a side effect of the compression — shifts pitch
  // UP by `p`. Net: pitch shifted by `p`, duration unchanged.
  const stretched = timeStretch(pcm, p);
  return linearResample(stretched, pcm.length);
}
